import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Check, Copy } from "lucide-react";
import * as React from "react";
import { cn, initials } from "@/lib/utils";

export function Avatar({
  name, email, className,
}: {
  name?: string | null;
  email?: string | null;
  className?: string;
}) {
  return (
    <AvatarPrimitive.Root
      className={cn(
        "inline-flex size-7 shrink-0 select-none items-center justify-center overflow-hidden",
        "rounded-full bg-accent-3 text-2xs font-medium text-accent-11",
        className,
      )}
    >
      <AvatarPrimitive.Fallback>{initials(name || email)}</AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

export function Separator({
  className, orientation = "horizontal", ...props
}: React.ComponentProps<typeof SeparatorPrimitive.Root>) {
  return (
    <SeparatorPrimitive.Root
      orientation={orientation}
      className={cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-px w-full" : "h-full w-px",
        className,
      )}
      {...props}
    />
  );
}

export function Switch({ className, ...props }: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      className={cn(
        "peer inline-flex h-4.5 w-8 shrink-0 cursor-pointer items-center rounded-full border-2",
        "border-transparent transition-colors",
        "data-[state=checked]:bg-accent-9 data-[state=unchecked]:bg-gray-6",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          "pointer-events-none block size-3.5 rounded-full bg-white shadow-sm ring-0",
          "transition-transform data-[state=checked]:translate-x-3.5",
          "data-[state=unchecked]:translate-x-0",
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export const TooltipProvider = TooltipPrimitive.Provider;

export function Tooltip({
  children, content, side = "top", open, onOpenChange, variant = "chip",
}: {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  /** Control it from outside — for a trigger whose tooltip must stay shut at times, like the
   *  phone rail's while the popover it opens is showing. Leave both unset otherwise. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * `chip` is the default: a dark pill for a few words of explanation.
   *
   * `card` is for content that is laid out rather than read — a panel of figures. It is a
   * surface, not an inversion, so the numbers in it keep the colours they have everywhere else;
   * a severity shown in danger red on a near-black pill would be a different red from the one in
   * the table. It brings no padding, because content that is laid out owns its own.
   */
  variant?: "chip" | "card";
}) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            "z-50 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0",
            variant === "card"
              ? "overflow-hidden rounded-lg border border-border bg-surface-raised text-text shadow-lg"
              : "max-w-xs rounded-md bg-gray-12 px-2 py-1 text-xs text-gray-1 shadow-md dark:bg-gray-4 dark:text-gray-12",
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const Tabs = TabsPrimitive.Root;

export function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn("flex items-center gap-4 border-b border-border", className)}
      {...props}
    />
  );
}

export function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        "relative -mb-px border-b-2 border-transparent px-0.5 pb-2 pt-1 text-sm font-medium",
        "text-text-muted transition-colors hover:text-text",
        "data-[state=active]:border-accent-9 data-[state=active]:text-text",
        className,
      )}
      {...props}
    />
  );
}

export const TabsContent = TabsPrimitive.Content;

/**
 * Copy-to-clipboard. Confirms in place for a second and a half rather than firing a toast:
 * copying an extension is a low-stakes action that should not interrupt anything, but silent
 * failure would leave people pasting stale clipboard contents.
 */
export function CopyButton({
  value, className, label = "Copy",
}: {
  value: string;
  className?: string;
  label?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch {
          // Clipboard is blocked without a secure context or user gesture; select the text
          // instead of pretending it worked.
          window.prompt("Copy:", value);
        }
      }}
      className={cn(
        "inline-flex items-center justify-center rounded p-1 text-text-subtle transition-colors",
        "hover:bg-surface-hover hover:text-text",
        className,
      )}
      aria-label={copied ? "Copied" : label}
    >
      {copied ? <Check className="size-3.5 text-success-9" /> : <Copy className="size-3.5" />}
    </button>
  );
}
